import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { useAppContext } from "./useAppContext";
import { useErrorHandling } from "./useErrorHandling";
import { useStableCallback } from "./useStableCallback";

/** Remove undefined if a default is specified  */
type LabelResult<T extends eXpress.creator.LabelRequest | undefined> = T extends undefined
	? undefined
	: string;

export function useLabels<T extends eXpress.creator.LabelRequest>(
	labelsRequests: [T],
): [translated: [LabelResult<T>], isLoading: boolean];

// I don't thing we can map variable length tuples yet
export function useLabels<
	T1 extends eXpress.creator.LabelRequest,
	T2 extends eXpress.creator.LabelRequest,
>(labelsRequests: [T1, T2]): [translated: [LabelResult<T1>, LabelResult<T2>], isLoading: boolean];

export function useLabels<
	T1 extends eXpress.creator.LabelRequest,
	T2 extends eXpress.creator.LabelRequest,
	T3 extends eXpress.creator.LabelRequest,
>(
	labelsRequests: [T1, T2, T3],
): [translated: [LabelResult<T1>, LabelResult<T2>, LabelResult<T3>], isLoading: boolean];

export function useLabels<
	T1 extends eXpress.creator.LabelRequest | undefined,
	T2 extends eXpress.creator.LabelRequest | undefined,
	T3 extends eXpress.creator.LabelRequest | undefined,
	T4 extends eXpress.creator.LabelRequest | undefined,
>(
	labelsRequests: [T1, T2, T3, T4],
): [
	translated: [LabelResult<T1>, LabelResult<T2>, LabelResult<T3>, LabelResult<T4>],
	isLoading: boolean,
];

export function useLabels(
	labelsRequests: ReadonlyArray<eXpress.creator.LabelRequest | undefined>,
): [translated: (string | undefined)[], isLoading: boolean];

export function useLabels(labels: ReadonlyArray<eXpress.creator.LabelRequest | undefined>) {
	const { localizedContent, reactQueryNoReloadOptions } = useAppContext();

	const labelsToFetch = labels.filter((labelRequest) => {
		if (!labelRequest) return false;

		// log missing label warning
		localizedContent.isKeyMissingOnServer(labelRequest.label, {
			logMissingToServer: true,
			...labelRequest,
		});

		return !localizedContent.isKeyFetched(labelRequest.label);
	}) as eXpress.creator.LabelRequest[];

	// useQuery will trigger a rerender when getKeysAsync is ready.
	// This will cause this hook to return the new values
	const queryResult = useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: ["useLabels", ...labels], // use labels (not labelsToFetch) because otherwise the key changes and useQuery state will be a new idle state
		queryFn: () => localizedContent.getKeysAsync(labelsToFetch),
		...reactQueryNoReloadOptions,
		gcTime: 0, // Disable caching (we don't need cache, we only query non-cached items for localizedContent)
		enabled: labelsToFetch.length > 0,
	});

	const currentValues = labels.map((labelRequest) => {
		if (!labelRequest) return "";
		// Uses empty string as default so no 'string | undefined' needs to be returned as type.
		// contra: caller cannot know if a label was not found on the server or had a value of "" (I think not yet needed a the moment)
		const defaultValue =
			labelRequest.default ?? localizedContent.defaults[labelRequest.label] ?? "";

		// Don't use result of query, so the default is used from the labelRequest argument
		return localizedContent.getMaybe(labelRequest.label, defaultValue, {
			logMissingToServer: false, // No need to logger, it is checked/logged during the fetch
		});
	});

	return [currentValues, queryResult.isLoading];
}

/** Prefetch labels without rerender when ready */
export function usePrefetchLabels(
	labelsRequests:
		| ReadonlyArray<eXpress.creator.LabelRequest | undefined>
		| eXpress.creator.LabelRequest,
) {
	const labels: (eXpress.creator.LabelRequest | undefined)[] = eXpress.utils.isArray(
		labelsRequests,
	)
		? labelsRequests
		: [labelsRequests];

	// Only extract the keys
	const keys = labels
		.map((requestedLabel) => requestedLabel?.label ?? "") // ?? = to satisfy TS, normally never undefined
		.sort(); // so same order in useEffect dependency list

	const { localizedContent } = useAppContext();
	const { createErrorHandler } = useErrorHandling();

	// TODO: use useEffectEvent
	const refetch = useStableCallback(() => {
		localizedContent.prefetchLabelsAsync(keys).catch(createErrorHandler());
	});

	/** keys as string so I can use it in the dependency array */
	const keysString = keys.join(",");

	// When labels change, check to refetch
	useEffect(() => {
		refetch();
	}, [keysString, refetch]);
}
